import {Component, inject, OnInit} from '@angular/core';
import {ActivatedRoute, ActivatedRouteSnapshot, RedirectCommand, ResolveFn, Router, RouterOutlet} from '@angular/router';
import {combineLatest, takeUntil} from 'rxjs';
import {TranslatePipe} from '@ngx-translate/core';
import {AuthenticatedBaseComponent} from '../../authenticated-base.component';
import {TabSwitcherComponent} from '../../../components/tab-switcher/tab-switcher.component';
import {TabSwitcherConfig} from '../../../components/tab-switcher/tab-switcher-configuration.interface';
import {BackButtonComponent} from '../../../components/back-button/back-button.component';
import {UserSessionService} from '../../../services/user-session-service/user-session.service';
import {PERMISSION, ServiceProviderHub} from '@generated-backend-types';
import Event = ServiceProviderHub.App.Http.Resources.ServiceProviderHub.Eventmanagement.Event.Event;
import EventInquiry = ServiceProviderHub.App.Http.Resources.ServiceProviderHub.Eventmanagement.Event.Inquiry.EventInquiry;
import EventOverviewSnapshot = ServiceProviderHub.App.Http.Resources.ServiceProviderHub.Eventmanagement.Event.EventOverviewSnapshot.EventOverviewSnapshot;

export const requireSnapshotInquiry: ResolveFn<EventInquiry | RedirectCommand> = (route: ActivatedRouteSnapshot) => {
  const inquiry: EventInquiry | null | undefined = route.parent?.data['inquiry'];
  if (inquiry) {
    return inquiry;
  }
  const eventId = route.pathFromRoot.find(r => r.routeConfig?.path === ':id')?.params['id'];
  const snapshotId = route.parent?.params['snapshotId'];
  return new RedirectCommand(inject(Router).createUrlTree(['/events', eventId, 'event-overview-snapshots', snapshotId, 'event-overview']));
};

@Component({
  selector: 'app-event-overview-snapshot-detail-shell',
  templateUrl: './event-overview-snapshot-detail-shell.component.html',
  imports: [RouterOutlet, TabSwitcherComponent, BackButtonComponent, TranslatePipe],
  standalone: true,
})
export class EventOverviewSnapshotDetailShellComponent extends AuthenticatedBaseComponent implements OnInit {
  public event?: Event;
  public snapshot?: EventOverviewSnapshot | null;
  public inquiry?: EventInquiry | null;
  public notNominated = false;
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private userSessionService = inject(UserSessionService);

  public get tabConfig(): TabSwitcherConfig[] {
    if (!this.event || !this.snapshot) return [];
    const base = `/events/${this.event.id}/event-overview-snapshots/${this.snapshot.id}`;
    const tabs: TabSwitcherConfig[] = [
      {link: `${base}/event-overview`, translationKey: 'events.detail.inquiry.tabs.event_overview', exact: false},
    ];
    if (this.inquiry && this.userSessionService.hasAllPermission([PERMISSION.SPH_RESOURCES_SERVICEPROVIDER_SERVICEPROVIDERINQUIRY_READ_ALL])) {
      tabs.push({link: `${base}/offer`, translationKey: 'events.detail.inquiry.tabs.offer', exact: false});
    }
    return tabs;
  }

  public ngOnInit(): void {
    this.titleService.setTitle('events.detail.event_overview_snapshot.title');
    const eventRoute = this.activatedRoute.pathFromRoot.find(r => r.routeConfig?.path === ':id');
    if (!eventRoute) {
      throw new Error('EventOverviewSnapshotDetailShellComponent: cannot locate route ancestor with path \':id\'');
    }
    combineLatest([eventRoute.data, this.activatedRoute.data]).pipe(takeUntil(this.onDestroy$)).subscribe(([eventData, data]) => {
      this.event = eventData['event'];
      this.snapshot = data['snapshot'];
      this.inquiry = data['inquiry'];
      this.notNominated = this.event?.is_nominated === false;
      if (!this.snapshot && !this.notNominated && this.event) {
        this.router.navigate(['/events', this.event.id, 'event-overview-snapshots']).then();
      }
      this.cdr.markForCheck();
    });
  }

  public override ngAfterViewInit(): void {
    this.contentHeaderService.setContentHeaderFallback(this.contentHeaderTemplate);
  }
}
